import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { celebrate } from "@/lib/celebrate";
import { useLang } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Cloud, Server, ShieldCheck, KeyRound, Check, Loader2, ExternalLink, Zap, Mailbox, Copy } from "lucide-react";
import { ExoLeastPrivilege } from "@/components/ExoLeastPrivilege";
import { SelfHostedGraphSetup } from "@/components/SelfHostedGraphSetup";

const STEPS = ["wizard.stepMode", "wizard.stepGraph", "wizard.stepSso", "wizard.stepExchange"];
const ENTRA_ROLES_URL = "https://entra.microsoft.com/#view/Microsoft_AAD_IAM/RolesManagementMenuBlade/~/AllRoles";

export function SetupWizard({ open, onOpenChange, status, initialStep, onComplete }) {
  const { t } = useLang();
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState(status?.operationMode || "cloudOnly");
  const [ssoOnly, setSsoOnly] = useState(!!status?.localLoginDisabled);
  const [ssoEnabled, setSsoEnabled] = useState(status?.ssoLoginEnabled != null ? !!status.ssoLoginEnabled : true);
  const [graphConsented, setGraphConsented] = useState(!!status?.graphConsented);
  const [cloudSyncDetected, setCloudSyncDetected] = useState(!!status?.cloudSyncEnabled);
  const [busy, setBusy] = useState(false);
  const [perms, setPerms] = useState(null); // {granted:[], missingRequired:[]} | 'loading' | null
  const [exoRoleReady, setExoRoleReady] = useState(false);
  const [graphAppId, setGraphAppId] = useState(status?.graphAppId || null);
  const [selfGraph, setSelfGraph] = useState(null); // self-hosted {ok, missing:[]}

  useEffect(() => {
    if (open) {
      setStep(initialStep != null ? initialStep : (status?.lastStep || 0));
      setMode(status?.operationMode || "cloudOnly");
      setSsoOnly(status?.localLoginDisabled != null ? !!status.localLoginDisabled : true);
      setSsoEnabled(status?.ssoLoginEnabled != null ? !!status.ssoLoginEnabled : true);
      setGraphConsented(!!status?.graphConsented);
    }
  }, [open, status, initialStep]);

  const total = STEPS.length;

  // Step 2 (Graph) hard gate: block "Next" until Graph is actually connected. Self-hosted uses the
  // live test-graph result (token acquired); SaaS uses admin-consent state. Missing individual
  // permissions are surfaced as a soft warning below — they do not hard-block a working connection.
  const selfHosted = status?.saasMode === false;
  const graphReady = selfHosted ? !!selfGraph?.ok : !!graphConsented;

  // Auto-advance: the moment Graph turns green on the Graph step, flash a confirmation and move to
  // the next step. Fires only on the false→true transition and once per wizard session (so an admin
  // who clicks Back to review Graph is not bounced forward again).
  const autoAdvancedRef = useRef(false);
  const prevReadyRef = useRef(false);
  useEffect(() => {
    if (open) { autoAdvancedRef.current = false; prevReadyRef.current = graphReady; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (open && step === 1 && graphReady && !prevReadyRef.current && !autoAdvancedRef.current) {
      autoAdvancedRef.current = true;
      toast.success(t("wizard.graphAutoAdvance"));
      saveProgress(2);
      const id = setTimeout(() => setStep((s) => (s === 1 ? 2 : s)), 1200);
      prevReadyRef.current = graphReady;
      return () => clearTimeout(id);
    }
    prevReadyRef.current = graphReady;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step, graphReady]);

  const saveProgress = (nextStep, extra = {}) => {
    api.post("/wizard/progress", { step: nextStep, ...extra }).catch(() => {});
  };

  // Guided Graph re-check: while on the consent step, poll for consent and re-check when the
  // admin returns to this tab after granting it in Microsoft — so the step turns green on its own.
  const silentRecheck = async () => {
    try {
      const { data } = await api.get("/auth/sso/status");
      if (data?.graphConsented) setGraphConsented(true);
      if (data?.cloudSyncEnabled) setCloudSyncDetected(true);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    if (!open || step !== 1 || graphConsented) return;
    const id = setInterval(silentRecheck, 4000);
    const onFocus = () => silentRecheck();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step, graphConsented]);

  // Deep-confirm: once directory access is granted, fetch the exact permissions the platform holds.
  useEffect(() => {
    if (!open || step !== 1 || !graphConsented) { return; }
    let cancelled = false;
    setPerms("loading");
    api.get("/wizard/graph-permissions")
      .then((r) => { if (!cancelled) setPerms(r.data); })
      .catch(() => { if (!cancelled) setPerms(null); });
    return () => { cancelled = true; };
  }, [open, step, graphConsented]);

  const saveMode = async () => {
    setBusy(true);
    try {
      await api.put("/settings", { operationMode: mode });
      saveProgress(1, { modeChosen: true });
      toast.success(t("wizard.saved"));
      setStep(1);
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  const grantGraph = async () => {
    try {
      // Launch consent in the SAME tab tagged origin=wizard so the callback returns to the wizard.
      const { data } = await api.get("/auth/sso/graph-admin-consent?origin=wizard");
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const recheckGraph = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/auth/sso/status");
      setGraphConsented(!!data?.graphConsented);
      if (data?.cloudSyncEnabled) setCloudSyncDetected(true);
      if (!data?.graphConsented) toast.message(t("wizard.graphPending"));
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  // Step 4 (Exchange Online, REQUIRED in SaaS): enable EXO and learn the vendor app id. The guided
  // least-privilege panel (ExoLeastPrivilege) owns the escalate → downgrade → verify polling and reports
  // back via onStatus so the footer can flip between "Remind me later" and "Finish".
  useEffect(() => {
    if (!open || step !== 3) return;
    api.put("/settings", { exo: { enabled: true } }).catch(() => {});
    api.get("/auth/sso/status").then((r) => setGraphAppId(r.data?.graphAppId)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step]);

  const onExoStatus = (d) => {
    setExoRoleReady(!!(d?.recipientAccess || d?.appPolicyCmdlets));
  };

  const saveSso = async () => {
    setBusy(true);
    try {
      await api.put("/settings", {
        sso: { enabled: ssoEnabled },
        security: { localLoginDisabled: ssoEnabled && ssoOnly, ssoDefaultRole: "viewer" },
      });
      saveProgress(3, { ssoConfigured: true });
      setStep(3);
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  const finish = async () => {
    setBusy(true);
    try {
      await api.put("/settings", { exo: { enabled: true } });
      try { await api.post("/settings/exo/test"); } catch { /* org derive/connect best-effort */ }
      saveProgress(3, { ssoConfigured: true, exoConfigured: true });
      await api.post("/wizard/complete");
      toast.success(t("wizard.completed"));
      celebrate();
      onOpenChange(false);
      onComplete && onComplete();
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto" data-testid="setup-wizard">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black uppercase tracking-tight">{t("wizard.title")}</DialogTitle>
          <DialogDescription>{t("wizard.subtitle")}</DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="mb-2 flex items-center gap-2" data-testid="wizard-stepper">
          {STEPS.map((s, i) => (
            <div key={s} className="flex flex-1 items-center gap-2">
              <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${i < step ? "bg-emerald-600 text-white" : i === step ? "bg-zinc-900 text-white" : "bg-zinc-200 text-zinc-500"}`}>
                {i < step ? <Check size={14} /> : i + 1}
              </div>
              <span className={`hidden truncate text-xs font-medium sm:block ${i === step ? "text-zinc-900" : "text-zinc-400"}`}>{t(s)}</span>
              {i < STEPS.length - 1 && <div className="h-px flex-1 bg-zinc-200" />}
            </div>
          ))}
        </div>
        <p className="text-xs font-medium text-zinc-400">{t("wizard.step").replace("{n}", step + 1).replace("{total}", total)}</p>

        {/* Step 1 — mode */}
        {step === 0 && (
          <div className="space-y-3" data-testid="wizard-step-mode">
            <div>
              <h3 className="font-bold text-zinc-900">{t("wizard.modeTitle")}</h3>
              <p className="text-sm text-zinc-500">{t("wizard.modeDesc")}</p>
            </div>
            <RadioGroup value={mode} onValueChange={setMode} className="gap-3">
              {[
                { v: "cloudOnly", icon: Cloud, tt: "wizard.modeCloud", td: "wizard.modeCloudDesc", tid: "wizard-mode-cloud" },
                { v: "hybrid", icon: Server, tt: "wizard.modeHybrid", td: "wizard.modeHybridDesc", tid: "wizard-mode-hybrid" },
              ].map((o) => (
                <label key={o.v} htmlFor={o.v} data-testid={o.tid}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${mode === o.v ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-300"}`}>
                  <RadioGroupItem value={o.v} id={o.v} className="mt-1" />
                  <o.icon size={20} className="mt-0.5 shrink-0 text-zinc-700" />
                  <div>
                    <div className="font-semibold text-zinc-900">{t(o.tt)}</div>
                    <div className="text-sm text-zinc-500">{t(o.td)}</div>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>
        )}

        {/* Step 2 — graph */}
        {step === 1 && (
          <div className="space-y-3" data-testid="wizard-step-graph">
            <div>
              <h3 className="font-bold text-zinc-900">{t("wizard.graphTitle")}</h3>
              <p className="text-sm text-zinc-500">{t("wizard.graphDesc")}</p>
            </div>
            {status?.saasMode === false ? (
              <SelfHostedGraphSetup wizard onConnected={() => setGraphConsented(true)} onStatus={setSelfGraph} />
            ) : (
            <>
            <div className={`flex items-center gap-2 rounded-lg border p-3 text-sm font-medium ${graphConsented ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid="wizard-graph-status">
              <ShieldCheck size={16} className="shrink-0" />
              {graphConsented ? t("wizard.graphConsented") : t("wizard.graphPending")}
            </div>
            {graphConsented && cloudSyncDetected && (
              <div className="flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm font-medium text-sky-800" data-testid="wizard-cloudsync-detected">
                <Zap size={16} className="shrink-0" />
                {t("wizard.cloudSyncDetected")}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={grantGraph} data-testid="wizard-graph-consent-btn">
                <ExternalLink size={15} className="mr-1.5" /> {t("wizard.graphConsentBtn")}
              </Button>
              <Button variant="ghost" onClick={recheckGraph} disabled={busy} data-testid="wizard-graph-recheck-btn">
                {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null} {t("wizard.graphRecheck")}
              </Button>
            </div>
            {graphConsented && (
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3" data-testid="wizard-granted-perms">
                <div className="mb-1 flex items-center gap-1.5 text-sm font-bold text-zinc-800">
                  <ShieldCheck size={15} className="text-emerald-600" /> {t("wizard.grantedPerms")}
                </div>
                {perms === "loading" && <p className="text-sm text-zinc-500">{t("wizard.permLoading")}</p>}
                {perms && perms !== "loading" && (
                  <>
                    <p className="mb-2 text-xs text-zinc-500">{t("wizard.grantedPermsDesc")}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(perms.granted || []).slice(0, 8).map((p) => (
                        <span key={p} data-testid={`perm-chip-${p}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                          <Check size={11} /> {p}
                        </span>
                      ))}
                      {(perms.granted || []).length > 8 && (
                        <span className="inline-flex items-center rounded-full bg-zinc-200 px-2 py-0.5 text-xs font-medium text-zinc-600">
                          {t("wizard.permMore").replace("{n}", (perms.granted.length - 8))}
                        </span>
                      )}
                    </div>
                    {(perms.missingRequired || []).length > 0 && (
                      <p className="mt-2 text-xs font-medium text-amber-700" data-testid="wizard-perm-missing">{t("wizard.permMissing")}</p>
                    )}
                  </>
                )}
              </div>
            )}
            </>
            )}
          </div>
        )}

        {/* Step 3 — sso */}
        {step === 2 && (
          <div className="space-y-3" data-testid="wizard-step-sso">
            <div>
              <h3 className="font-bold text-zinc-900">{t("wizard.ssoTitle")}</h3>
              <p className="text-sm text-zinc-500">{t("wizard.ssoDesc")}</p>
            </div>
            <div className="flex items-start justify-between gap-3 rounded-lg border border-zinc-200 p-3">
              <div className="flex items-start gap-3">
                <KeyRound size={20} className="mt-0.5 shrink-0 text-zinc-700" />
                <div>
                  <Label htmlFor="wizard-sso-enable" className="font-semibold text-zinc-900">{t("wizard.ssoEnable")}</Label>
                  <p className="text-sm text-zinc-500">{t("wizard.ssoEnableDesc")}</p>
                </div>
              </div>
              <Switch id="wizard-sso-enable" checked={ssoEnabled}
                onCheckedChange={(v) => { setSsoEnabled(v); if (!v) setSsoOnly(false); }}
                data-testid="wizard-sso-enable-switch" />
            </div>
            <div className={`flex items-start justify-between gap-3 rounded-lg border border-zinc-200 p-3 ${ssoEnabled ? "" : "opacity-50"}`}>
              <div className="flex items-start gap-3">
                <ShieldCheck size={20} className="mt-0.5 shrink-0 text-zinc-700" />
                <div>
                  <Label htmlFor="wizard-sso-only" className="font-semibold text-zinc-900">{t("wizard.ssoOnly")}</Label>
                  <p className="text-sm text-zinc-500">{t("wizard.ssoOnlyDesc")}</p>
                </div>
              </div>
              <Switch id="wizard-sso-only" checked={ssoOnly} disabled={!ssoEnabled}
                onCheckedChange={setSsoOnly} data-testid="wizard-sso-only-switch" />
            </div>
            <div className="rounded-lg bg-zinc-50 p-3 text-sm text-zinc-600" data-testid="wizard-sso-role-info">
              {t("wizard.ssoRoleInfo")}
            </div>
          </div>
        )}

        {/* Step 4 — Exchange Online (guided least-privilege setup) */}
        {step === 3 && (
          <div className="space-y-3" data-testid="wizard-step-exchange">
            <div>
              <h3 className="font-bold text-zinc-900">{t("wizard.lpTitle")}</h3>
              <p className="text-sm text-zinc-500">{t("wizard.exchangeDesc")}</p>
            </div>
            <ExoLeastPrivilege appId={graphAppId} onStatus={onExoStatus} />
            <p className="text-xs text-zinc-500" data-testid="wizard-exchange-remind-hint">{t("wizard.exchangeRemindHint")}</p>
          </div>
        )}

        {/* Footer nav */}
        <div className="mt-2 flex items-center justify-between">
          <Button variant="ghost" onClick={() => { if (step === 0) { onOpenChange(false); } else { saveProgress(step - 1); setStep(step - 1); } }} data-testid="wizard-back-btn">
            {step === 0 ? t("wizard.cancel") : t("wizard.back")}
          </Button>
          {step === 0 && (
            <Button onClick={saveMode} disabled={busy} data-testid="wizard-next-btn">
              {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null} {t("wizard.next")}
            </Button>
          )}
          {step === 1 && (
            <div className="flex flex-col items-end gap-1">
              <Button onClick={() => { saveProgress(2); setStep(2); }} disabled={!graphReady} data-testid="wizard-next-btn">{t("wizard.next")}</Button>
              {!graphReady && <span className="text-right text-[11px] font-medium text-amber-600" data-testid="wizard-graph-gate-hint">{t("wizard.graphGate")}</span>}
            </div>
          )}
          {step === 2 && (
            <Button onClick={saveSso} disabled={busy} data-testid="wizard-next-btn">
              {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null} {t("wizard.next")}
            </Button>
          )}
          {step === 3 && (
            <Button onClick={finish} disabled={busy} data-testid="wizard-finish-btn">
              {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null}
              {exoRoleReady ? t("wizard.finish") : t("wizard.exchangeRemindLater")}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
